import * as immutable from 'object-path-immutable';

import { CLIENT_SETTINGS_PREFIX, clientSettingsBuildMetaMap } from "api";
import { ClientAction } from "state/action";
import { SettingsState } from "state/settings/state";

const emptySettings = {
    node: {
        loadingValues: false,
        loadedValues: false,
        conflict: false,
        values: new Map(),
        loadingMeta: false,
        loadedMeta: false,
        meta: new Map()
    },
    client: {
        loadingValues: false,
        loadedValues: false,
        conflict: false,
        values: new Map(),
        meta: clientSettingsBuildMetaMap()
    },
    updating: false,
    grants: {
        loading: false,
        loaded: false,
        grants: [],
        dialog: {
            show: false,
            nodeName: "",
            grant: null,
            updating: false
        }
    },
    tokens: {
        loading: false,
        loaded: false,
        tokens: [],
        dialog: {
            show: false,
            token: null,
            updating: false,
            newToken: null
        }
    },
    deleteNode: {
        loading: false,
        loaded: false,
        requested: false,
        updating: false
    }
};

const initialState: SettingsState = {
    tab: "profile" as const,
    sheet: "",
    ...emptySettings,
    formId: 0
};

export default (state: SettingsState = initialState, action: ClientAction): SettingsState => {
    switch (action.type) {
        case "SETTINGS_GO_TO_TAB":
            return immutable.wrap(state)
                .set("tab", action.payload.tab)
                .set("sheet", "")
                .set("node.conflict", false)
                .set("client.conflict", false)
                .update("formId", formId => formId + 1)
                .value();

        case "SETTINGS_GO_TO_SHEET":
            return immutable.wrap(state)
                .set("sheet", action.payload.sheet)
                .set("node.conflict", false)
                .set("client.conflict", false)
                .value();

        case "DISCONNECTED_FROM_HOME":
            return {
                ...state,
                ...emptySettings
            };

        case "SETTINGS_NODE_VALUES_LOAD":
            return immutable.set(state, "node.loadingValues", true);

        case "SETTINGS_NODE_VALUES_LOADED": {
            let values = new Map();
            action.payload.settings.forEach(({name, value}) => values.set(name, value));
            return immutable.wrap(state)
                .set("node.loadingValues", false)
                .set("node.loadedValues", true)
                .set("node.values", values)
                .update("formId", formId => formId + 1)
                .value();
        }

        case "SETTINGS_NODE_VALUES_LOAD_FAILED":
            return immutable.set(state, "node.loadingValues", false);

        case "SETTINGS_NODE_CONFLICT":
            return immutable.set(state, "node.conflict", true);

        case "SETTINGS_NODE_CONFLICT_CLOSE":
            return immutable.set(state, "node.conflict", false);

        case "SETTINGS_NODE_META_LOAD":
            return immutable.set(state, "node.loadingMeta", true);

        case "SETTINGS_NODE_META_LOADED": {
            const metadata = new Map();
            action.payload.meta.forEach(meta => metadata.set(meta.name, meta));
            return immutable.wrap(state)
                .set("node.loadingMeta", false)
                .set("node.loadedMeta", true)
                .set("node.meta", metadata)
                .update("formId", formId => formId + 1)
                .value();
        }

        case "SETTINGS_NODE_META_LOAD_FAILED":
            return immutable.set(state, "node.loadingMeta", false);

        case "SETTINGS_CLIENT_VALUES_LOAD":
            return immutable.set(state, "client.loadingValues", true);

        case "SETTINGS_CLIENT_VALUES_LOADED": {
            const values = new Map(state.client.values.entries());
            action.payload.settings.forEach(({name, value}) => values.set(name, value ?? null));

            const istate = immutable.wrap(state);
            if (!action.payload.stored) {
                istate.set("client.loadingValues", false);
            }
            istate.set("client.loadedValues", true)
                .set("client.values", values)
                .update("formId", formId => formId + 1);
            return istate.value();
        }

        case "SETTINGS_CLIENT_VALUES_LOAD_FAILED":
            return immutable.set(state, "client.loadingValues", false);

        case "SETTINGS_CLIENT_CONFLICT":
            return immutable.set(state, "client.conflict", true);

        case "SETTINGS_CLIENT_CONFLICT_CLOSE":
            return immutable.set(state, "client.conflict", false);

        case "SETTINGS_UPDATE":
            return {
                ...state,
                updating: true
            };

        case "SETTINGS_UPDATE_SUCCEEDED":
            const nodeValues = new Map(state.node.values);
            const clientValues = new Map(state.client.values);
            action.payload.settings.forEach(({name, value}) => {
                if (name.startsWith(CLIENT_SETTINGS_PREFIX)) {
                    if (value != null) {
                        clientValues.set(name, value);
                    } else {
                        const meta = state.client.meta.get(name);
                        if (meta?.defaultValue != null) {
                            clientValues.set(name, meta.defaultValue);
                        }
                    }
                } else {
                    if (value != null) {
                        nodeValues.set(name, value);
                    } else {
                        const meta = state.node.meta.get(name);
                        if (meta?.defaultValue != null) {
                            nodeValues.set(name, meta.defaultValue);
                        }
                    }
                }
            });
            return immutable.wrap(state)
                .set("node.conflict", false)
                .set("client.conflict", false)
                .set("node.values", nodeValues)
                .set("client.values", clientValues)
                .set("updating", false)
                .update("formId", formId => formId + 1)
                .value();

        case "SETTINGS_UPDATE_FAILED":
            return {
                ...state,
                updating: false
            };

        case "SETTINGS_GRANTS_LOAD":
            return immutable.set(state, "grants.loading", true);

        case "SETTINGS_GRANTS_LOADED":
            return immutable.assign(state, "grants", {
                loading: false,
                loaded: true,
                grants: action.payload.grants
            });

        case "SETTINGS_GRANTS_LOAD_FAILED":
            return immutable.set(state, "grants.loading", false);

        case "SETTINGS_GRANTS_DIALOG_OPEN":
            return immutable.assign(state, "grants.dialog", {
                show: true,
                nodeName: action.payload.nodeName,
                grant: action.payload.grant
            });

        case "SETTINGS_GRANTS_DIALOG_CLOSE":
            return immutable.set(state, "grants.dialog.show", false);

        case "SETTINGS_GRANTS_DIALOG_CONFIRM":
            return immutable.set(state, "grants.dialog.updating", true);

        case "SETTINGS_GRANTS_DIALOG_CONFIRMED":
            return immutable.wrap(state)
                .assign("grants.dialog", {
                    show: false,
                    updating: false
                })
                .set(
                    "grants.grants",
                    state.grants.grants
                        .map(g => g.nodeName !== action.payload.nodeName ? g : {...g, scope: action.payload.scope})
                        .filter(g => g.scope.length > 0)
                )
                .value();

        case "SETTINGS_GRANTS_DIALOG_CONFIRM_FAILED":
            return immutable.set(state, "grants.dialog.updating", false);

        case "SETTINGS_GRANTS_DELETED":
            return immutable.set(
                state,
                "grants.grants",
                state.grants.grants.filter(g => g.nodeName !== action.payload.nodeName)
            );

        case "EVENT_HOME_GRANT_UPDATED": {
            const {grant} = action.payload;

            const grants = state.grants.grants.filter(g => g.nodeName !== grant.nodeName);
            if (grant.scope.length > 0) {
                grants.push(grant);
                grants.sort((ga, gb) => ga.nodeName < gb.nodeName ? -1 : (ga.nodeName > gb.nodeName ? 1 : 0));
            }
            return immutable.set(state, "grants.grants", grants);
        }

        case "SETTINGS_TOKENS_LOAD":
            return immutable.set(state, "tokens.loading", true);

        case "SETTINGS_TOKENS_LOADED":
            return immutable.assign(state, "tokens", {
                loading: false,
                loaded: true,
                tokens: action.payload.tokens
            });

        case "SETTINGS_TOKENS_LOAD_FAILED":
            return immutable.set(state, "tokens.loading", false);

        case "SETTINGS_TOKENS_DIALOG_OPEN":
            return immutable.assign(state, "tokens.dialog", {
                show: true,
                token: action.payload.token
            });

        case "SETTINGS_TOKENS_DIALOG_CLOSE":
            return immutable.set(state, "tokens.dialog.show", false);

        case "SETTINGS_TOKENS_CREATE":
        case "SETTINGS_TOKENS_UPDATE":
            return immutable.set(state, "tokens.dialog.updating", true);

        case "SETTINGS_TOKENS_CREATED":
            return immutable.wrap(state)
                .assign("tokens.dialog", {
                    show: false,
                    updating: false,
                    newToken: action.payload.token
                })
                .set("tokens.tokens", [{
                    ...action.payload.token,
                    token: action.payload.token.token.substring(0, 4) + "\u2026"
                }].concat(state.tokens.tokens))
                .value();

        case "EVENT_HOME_TOKEN_ADDED":
            if (state.tokens.loaded) {
                return immutable.set(state, "tokens.tokens", [action.payload.token].concat(state.tokens.tokens));
            }
            return state;

        case "SETTINGS_TOKENS_UPDATED":
            return immutable.wrap(state)
                .assign("tokens.dialog", {
                    show: false,
                    updating: false
                })
                .set("tokens.tokens",
                    state.tokens.tokens.map(t => t.id === action.payload.token.id ? action.payload.token : t)
                )
                .value();

        case "EVENT_HOME_TOKEN_UPDATED":
            if (state.tokens.loaded) {
                return immutable.set(state, "tokens.tokens",
                    state.tokens.tokens.map(t => t.id === action.payload.token.id ? action.payload.token : t))
            }
            return state;

        case "SETTINGS_TOKENS_CREATE_FAILED":
        case "SETTINGS_TOKENS_UPDATE_FAILED":
            return immutable.set(state, "tokens.dialog.updating", false);

        case "SETTINGS_TOKENS_DELETED":
            return immutable.set(state, "tokens.tokens", state.tokens.tokens.filter(t => t.id !== action.payload.id));

        case "EVENT_HOME_TOKEN_DELETED":
            if (state.tokens.loaded) {
                return immutable.set(state, "tokens.tokens",
                    state.tokens.tokens.filter(t => t.id !== action.payload.id));
            }
            return state;

        case "SETTINGS_TOKENS_NEW_TOKEN_CLOSE":
            return immutable.set(state, "tokens.dialog.newToken", null);

        case "SETTINGS_DELETE_NODE_REQUEST_LOAD":
            return immutable.set(state, "deleteNode.loading", true);

        case "SETTINGS_DELETE_NODE_REQUEST_LOADED":
            return immutable.assign(state, "deleteNode", {
                loading: false,
                loaded: true,
                requested: action.payload.requested
            });

        case "SETTINGS_DELETE_NODE_REQUEST_LOAD_FAILED":
            return immutable.set(state, "deleteNode.loading", false);

        case "SETTINGS_DELETE_NODE_REQUEST_SEND":
        case "SETTINGS_DELETE_NODE_REQUEST_CANCEL":
            return immutable.set(state, "deleteNode.updating", true);

        case "SETTINGS_DELETE_NODE_REQUEST_UPDATE_FAILED":
            return immutable.set(state, "deleteNode.updating", false);

        case "SETTINGS_DELETE_NODE_REQUEST_STATUS_SET":
            return immutable.assign(state, "deleteNode", {
                updating: false,
                requested: action.payload.requested
            });

        case "EVENT_HOME_DELETE_NODE_STATUS_UPDATED":
            return immutable.set(state, "deleteNode.requested", action.payload.requested);

        default:
            return state;
    }
}
